import React from "react";
import { AccessTime, Done, DoneAll } from "@material-ui/icons";

// Constantes
import { ACK_STATUS, MESSAGE_MEDIA_TYPES } from "../constants/messageConstants";

/**
 * 📱 Componente MessageAck
 * 
 * Renderiza os indicadores de status de entrega/leitura das mensagens:
 * - ⏰ Pendente (relógio)
 * - ✓ Enviada (check simples)
 * - ✓✓ Entregue (check duplo)
 * - ✓✓ Lida (check duplo azul)
 * - ✓✓ Falhou (check duplo cinza)
 * 
 * @param {Object} message - Objeto da mensagem com status ack
 * @param {Object} classes - Classes de estilo do Material-UI
 */
const MessageAck = ({ message, classes }) => {
  const debugAck = typeof window !== 'undefined' && window.localStorage && window.localStorage.getItem('debugAck') === '1';
  // Não renderiza para mensagens recebidas
  if (!message.fromMe) {
    return null;
  }

  const isAudioMessage =
    message.mediaType === MESSAGE_MEDIA_TYPES.AUDIO ||
    message.mediaType === "audioMessage";
  const isAudioAlreadyPlayed =
    isAudioMessage &&
    (typeof message.ack === "number" && message.ack >= ACK_STATUS.PLAYED ||
      !!message.playedAt);

  // Novo pipeline: prioriza ACK numérico quando disponível;
  // depois status textual; depois timestamps.
  const status = (message.status || "").toLowerCase();
  const has = (v) => {
    if (v === null || v === undefined) return false;
    if (typeof v === 'string') return v.trim().length > 0 && v !== '0000-00-00 00:00:00';
    return !!v;
  };

  // Mapeamento por timestamps quando status não vier textual
  // sentAt -> Done, deliveredAt -> DoneAll, readAt/playedAt -> DoneAll (estilos especiais)
  const byTimestamps = () => {
    if (has(message.readAt)) {
      if (isAudioMessage && !isAudioAlreadyPlayed) {
        return <DoneAll fontSize="small" className={classes.ackIcons} />; // áudio lido, porém não reproduzido
      }
      return <DoneAll fontSize="small" className={classes.ackDoneAllIcon} />; // lido
    }
    if (has(message.playedAt)) {
      return <DoneAll fontSize="small" className={classes.ackDoneAllIcon} />; // reproduzido
    }
    if (has(message.deliveredAt)) {
      return <DoneAll fontSize="small" className={classes.ackIcons} />; // entregue
    }
    if (has(message.sentAt)) {
      return <Done fontSize="small" className={classes.ackIcons} />; // enviado
    }
    if (has(message.failedAt) || status === "failed") {
      // Mantemos o ícone duplo com classe neutra para não quebrar temas;
      // podemos ajustar a classe no futuro para um ícone de erro específico.
      return <DoneAll fontSize="small" className={classes.ackDoneAllIcon} />;
    }
    return null;
  };

  // 1) Se existir ACK numérico, prioriza-o
  if (typeof message.ack === 'number') {
    if (debugAck) console.groupCollapsed('[ACK] numeric', { id: message.id, ack: message.ack, status: message.status, fromMe: message.fromMe });
    switch (message.ack) {
      case ACK_STATUS.PENDING:
        return <AccessTime fontSize="small" className={classes.ackIcons} />;
      case ACK_STATUS.SENT:
        return <Done fontSize="small" className={classes.ackIcons} />;
      case ACK_STATUS.RECEIVED:
        return <DoneAll fontSize="small" className={classes.ackIcons} />;
      case ACK_STATUS.READ:
        if (isAudioMessage && !isAudioAlreadyPlayed) {
          return <DoneAll fontSize="small" className={classes.ackIcons} />;
        }
        return <DoneAll fontSize="small" className={classes.ackDoneAllIcon} />;
      case ACK_STATUS.PLAYED:
        return <DoneAll fontSize="small" className={classes.ackDoneAllIcon} />;
      case ACK_STATUS.FAILED:
        return <DoneAll fontSize="small" className={classes.ackDoneAllIcon} />;
      default:
        break;
    }
    if (debugAck) console.groupEnd();
  }

  // 2) Interpretação direta do status textual, se presente
  if (status) {
    if (debugAck) console.groupCollapsed('[ACK] textual', { id: message.id, status, fromMe: message.fromMe });
    switch (status) {
      case "pending":
      case "sending":
        return <AccessTime fontSize="small" className={classes.ackIcons} />;
      case "sent":
        return <Done fontSize="small" className={classes.ackIcons} />;
      case "delivered":
        return <DoneAll fontSize="small" className={classes.ackIcons} />;
      case "read":
        if (isAudioMessage && !isAudioAlreadyPlayed) {
          return <DoneAll fontSize="small" className={classes.ackIcons} />;
        }
        return <DoneAll fontSize="small" className={classes.ackDoneAllIcon} />;
      case "played":
        return <DoneAll fontSize="small" className={classes.ackDoneAllIcon} />;
      case "failed":
        return <DoneAll fontSize="small" className={classes.ackDoneAllIcon} />;
      default:
        // Se vier um status desconhecido, tentamos pelos timestamps
        return byTimestamps();
    }
  }

  // 3) Se não houver status textual, tentar pelos timestamps
  if (debugAck) console.groupCollapsed('[ACK] timestamps', { id: message.id, fromMe: message.fromMe, sentAt: message.sentAt, deliveredAt: message.deliveredAt, readAt: message.readAt, playedAt: message.playedAt });
  const tsIcon = byTimestamps();
  if (debugAck) console.groupEnd();
  if (tsIcon) return tsIcon;
  // 4) Sem fonte confiável, não renderiza para evitar antecipação incorreta
  return null;
};

export default MessageAck;
